import dayjs from 'dayjs';
import { format, isValid, parseISO } from 'date-fns';
import { v4 as uuid } from 'uuid';

import { AdminTypes } from '../../../components/Menu';
import { WEEKDAY_PRESETS } from './constants';
import {
	AiParsedForm,
	AiParsedSchedule,
	AuthPeriodKind,
	AuthPeriodOption,
	ClassifiedAuthorizations,
	ProviderOption,
	ScheduleBlockValues,
	ScheduleFormValues,
	ScheduleTimeSlotValues,
	SessionTypeOption,
	AuthorizationPeriod,
	WeekdayCode,
} from './types';
import { ActiveAuthorizationProperties } from '../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';

export const findAuthorizationPeriod = (
	periods: AuthorizationPeriod[],
	startDate: string,
): AuthorizationPeriod | undefined =>
	periods.find((period) => period.startDate <= startDate && period.endDate >= startDate)
		?? periods.find((period) => period.startDate > startDate);

export const emptyTimeSlot = (): ScheduleTimeSlotValues => ({
	uid: uuid(),
	days: [],
	startTime: '',
	endTime: '',
});

export const emptyScheduleBlock = (): ScheduleBlockValues => ({
	uid: uuid(),
	startDate: '',
	endDate: '',
	providerId: '',
	sessionTypeId: '',
	step: 1,
	timeSlots: [emptyTimeSlot()],
});

export const emptyFormValues = (): ScheduleFormValues => ({
	clientId: '',
	schedules: [emptyScheduleBlock()],
});

// Provider and session type (and their AI hints) are bound to the selected client, so a client
// change invalidates them. Reusable inputs (days, times, dates) are preserved.
export const clearClientBoundFields = (blocks: ScheduleBlockValues[]): ScheduleBlockValues[] =>
	blocks.map((block) => ({
		...block,
		providerId: '',
		sessionTypeId: '',
		providerHint: undefined,
		sessionTypeHint: undefined,
	}));

export const toggleDay = (days: WeekdayCode[], code: WeekdayCode): WeekdayCode[] =>
	days.includes(code) ? days.filter((d) => d !== code) : [...days, code].sort((a, b) => a - b) as WeekdayCode[];

const timeToMinutes = (hhmm: string): number => {
	const [h, m] = hhmm.split(':').map(Number);

	return h * 60 + m;
};

export const formatMinutesToTime = (minutes: number): string => {
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	const period = h >= 12 ? 'PM' : 'AM';
	const displayH = h % 12 || 12;

	return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
};

export const buildSchedulePayload = (values: ScheduleFormValues) =>
	values.schedules.map((block) => ({
		clientId: values.clientId,
		startDate: block.startDate,
		endDate: block.endDate,
		providerId: block.providerId,
		sessionTypeId: Number(block.sessionTypeId),
		timeSlots: block.timeSlots.flatMap((slot) =>
			[...slot.days].sort((a, b) => a - b).map((dayOfWeek) => ({
				id: slot.id,
				dayOfWeek,
				startTimeMinutes: timeToMinutes(slot.startTime),
				endTimeMinutes: timeToMinutes(slot.endTime),
			})),
		),
		frequency: 0,
		step: block.step,
		weekStartsOn: 0,
	}));

const DAY_TOKEN_MAP: Record<string, WeekdayCode> = {
	sun: 0, sunday: 0,
	mon: 1, monday: 1,
	tue: 2, tues: 2, tuesday: 2,
	wed: 3, weds: 3, wednesday: 3,
	thu: 4, thur: 4, thurs: 4, thursday: 4,
	fri: 5, friday: 5,
	sat: 6, saturday: 6,
};

export const mapDayTokensToCodes = (tokens?: string[] | null): WeekdayCode[] => {
	if (!tokens?.length) return [];
	const result = new Set<WeekdayCode>();

	tokens.forEach((raw) => {
		const token = raw.trim().toLowerCase();

		if (token === 'weekdays' || token === 'weekday') {
			WEEKDAY_PRESETS.weekdays.forEach((d) => result.add(d));
		} else if (token === 'weekends' || token === 'weekend') {
			WEEKDAY_PRESETS.weekends.forEach((d) => result.add(d));
		} else if (token === 'all' || token === 'all 7 days' || token === 'daily') {
			WEEKDAY_PRESETS.all.forEach((d) => result.add(d));
		} else if (token in DAY_TOKEN_MAP) {
			result.add(DAY_TOKEN_MAP[token]);
		}
	});

	return Array.from(result).sort((a, b) => a - b) as WeekdayCode[];
};

export const parseTimeTo24h = (raw?: string | null): string => {
	if (!raw) return '';

	const value = raw.trim().toUpperCase();
	const match = value.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/);

	if (!match) return '';

	let hours = parseInt(match[1], 10);
	const minutes = match[2] ? parseInt(match[2], 10) : 0;
	const meridiem = match[3];

	if (meridiem === 'PM' && hours < 12) hours += 12;

	if (meridiem === 'AM' && hours === 12) hours = 0;

	if (hours > 23 || minutes > 59) return '';

	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

export const normalizeDate = (raw?: string | null): string => {
	if (!raw) return '';

	const trimmed = raw.trim();
	const iso = parseISO(trimmed);

	if (isValid(iso) && /^\d{4}-\d{2}-\d{2}/.test(trimmed)) return format(iso, 'yyyy-MM-dd');

	const us = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);

	if (us) {
		const year = us[3].length === 2 ? `20${us[3]}` : us[3];
		const date = new Date(Number(year), Number(us[1]) - 1, Number(us[2]));

		if (isValid(date)) return format(date, 'yyyy-MM-dd');
	}

	return '';
};

const fuzzyFindId = <T extends { id: string | number; label: string }>(options: T[], name?: string | null): string => {
	if (!name) return '';

	const needle = name.trim().toLowerCase();

	if (!needle) return '';

	const exact = options.find((o) => o.label.toLowerCase() === needle);

	if (exact) return String(exact.id);

	const partial = options.find(
		(o) => o.label.toLowerCase().includes(needle) || needle.includes(o.label.toLowerCase()),
	);

	return partial ? String(partial.id) : '';
};

const aiScheduleToBlock = (schedule: AiParsedSchedule): ScheduleBlockValues => ({
	...emptyScheduleBlock(),
	startDate: normalizeDate(schedule.startDate),
	endDate: normalizeDate(schedule.endDate),
	timeSlots: schedule.timeSlots?.length ? schedule.timeSlots.map((timeSlot) => ({
		uid: uuid(),
		days: mapDayTokensToCodes(timeSlot.days),
		startTime: parseTimeTo24h(timeSlot.startTime),
		endTime: parseTimeTo24h(timeSlot.endTime),
	})) : [emptyTimeSlot()],
	providerHint: schedule.provider ?? undefined,
	sessionTypeHint: schedule.sessionType ?? undefined,
});

const scheduleGroupKey = (block: ScheduleBlockValues): string => [
	block.startDate,
	block.endDate,
	block.providerHint?.trim().toLowerCase() ?? '',
	block.sessionTypeHint?.trim().toLowerCase() ?? '',
].join('|');

export const resolveAiSchedules = (parsed: AiParsedForm): ScheduleBlockValues[] =>
	(parsed.schedules?.length ? parsed.schedules : [{}])
		.map(aiScheduleToBlock)
		.reduce<ScheduleBlockValues[]>((blocks, block) => {
			const matchingBlock = blocks.find((item) => scheduleGroupKey(item) === scheduleGroupKey(block));

			if (matchingBlock) {
				matchingBlock.timeSlots.push(...block.timeSlots);

				return blocks;
			}

			return [...blocks, block];
		}, []);

const BT_SESSION_TYPE_ENUM = 3; // SessionType.BehaviorTreatment

export const findBtDefaultSessionTypeId = (
	providerId: string,
	providers: ProviderOption[],
	sessionTypes: SessionTypeOption[],
): string => {
	const provider = providers.find((p) => p.id === providerId);

	if (provider?.roleSectionId !== AdminTypes.bt) return '';

	const btTypes = sessionTypes.filter((t) => t.type === BT_SESSION_TYPE_ENUM);

	return btTypes.length === 1 ? String(btTypes[0].id) : '';
};

export const resolveBlockHints = (
	block: ScheduleBlockValues,
	providers: ProviderOption[],
	sessionTypes: SessionTypeOption[],
): ScheduleBlockValues => {
	const next = { ...block };

	if (next.providerHint && !next.providerId) {
		const id = fuzzyFindId(providers, next.providerHint);

		if (id) {
			next.providerId = id;
			next.providerHint = undefined;
		}
	}

	if (next.sessionTypeHint && !next.sessionTypeId && next.providerId) {
		const id = fuzzyFindId(sessionTypes, next.sessionTypeHint);

		if (id) {
			next.sessionTypeId = id;
			next.sessionTypeHint = undefined;
		}
	}

	if (next.providerId && !next.sessionTypeId) {
		next.sessionTypeId = findBtDefaultSessionTypeId(next.providerId, providers, sessionTypes);
	}

	return next;
};

export const formatProviderLabel = (fullName: string, roleName: string) => `${fullName} (${roleName})`;

export interface DateDefaults {
	authEnd: string;
	defaultStart: string;
}

export const computeDateDefaultsForPeriod = (period: AuthPeriodOption | null): DateDefaults | null => {
	if (!period) return null;

	const today = dayjs().format('YYYY-MM-DD');
	const authEnd = dayjs(period.endDate).format('YYYY-MM-DD');
	const authStart = dayjs(period.startDate).format('YYYY-MM-DD');
	const defaultStart = authStart && today < authStart ? authStart : today;

	return { defaultStart, authEnd };
};

export const classifyAuthorizations = (authorizations: ActiveAuthorizationProperties[]): ClassifiedAuthorizations => {
	const today = dayjs().format('YYYY-MM-DD');
	const current: AuthPeriodOption[] = [];
	const future: AuthPeriodOption[] = [];

	authorizations.forEach((auth) => {
		const start = dayjs(auth.startDate).format('YYYY-MM-DD');
		const end = dayjs(auth.endDate).format('YYYY-MM-DD');
		const kind: AuthPeriodKind = start <= today && end >= today ? 'current' : 'future';

		const option: AuthPeriodOption = { id: auth.id, startDate: auth.startDate, endDate: auth.endDate, kind };

		if (kind === 'current') current.push(option);
		else if (start > today) future.push(option);
	});

	current.sort((a, b) => a.endDate.localeCompare(b.endDate));
	future.sort((a, b) => a.startDate.localeCompare(b.startDate));

	return { current, future };
};

export const pickDefaultAuthPeriod = (classified: ClassifiedAuthorizations): AuthPeriodOption | null => {
	if (classified.current.length) {
		return classified.current.reduce((latest, opt) =>
			opt.endDate > latest.endDate ? opt : latest,
		);
	}

	if (classified.future.length) return classified.future[0];

	return null;
};
